/**
 * 用語集（`listTerms`）と用語詳細（`getTermDetail`）の TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の
 * 対応をここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが
 * 付いた瞬間にキャッシュが分裂する。
 *
 * 案件を渡すと、一覧はその案件の未確定変更を重ねた姿（`side: 'after'`）を引く。詳細は
 * 変更前（`before`）と変更後（`after`）のどちらの面かを呼び出し側が選ぶ — 案件モードの
 * 用語詳細は両方を引いて左右に並べる。渡さなければ master だけを引く。**案件番号も用語の id も number である**（作り直しの契約）。URL は
 * 文字列しか運べないので、数に読めない値は案件を渡さなかったものとして扱う — 投げると
 * 手で URL を編集しただけで画面が失敗に落ちる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語集と用語詳細を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.listTerms / specGraph.getTermDetail
 */
import type { TermDetailOutput, TermListOutput } from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';
import type { PjSide } from './spec-graph-element-master-queries.js';

/**
 * URL の文字列を作り直しの id として読む。正の整数でなければ `undefined`。
 *
 * `Number('')` は `0`、`Number('6a')` は `NaN` になる。どちらも「案件を選んでいない」と
 * 同じ扱いにする — 契約は正の整数しか受けないので、そのまま渡すと入力検証で落ちて画面が
 * 失敗の見た目になる。
 *
 * @param raw URL から取った文字列
 * @returns 正の整数。読めなければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business URL の文字列を作り直しの id（正の整数）として読み、読めなければ未指定として扱う。
 * @graph-connects none
 */
export function toElementId(raw: string | undefined): number | undefined {
  if (raw === undefined || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * 用語集を取得する。
 *
 * @param pjNumber 重ねる案件番号。渡さなければ master
 * @returns 種別・値のタイプ・属するクラス・参照される数つきの用語の行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集を、master または指定した案件を適用した姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.listTerms
 */
export function useListTerms(pjNumber?: number): UseQueryResult<TermListOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.listTerms(locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.listTerms({
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side: 'after' as const } }),
      }),
  });
}

/**
 * 用語 1 件の詳細を取得する。
 *
 * `termId` が読めないときは問い合わせを止める。読めない id をそのまま撃つと、契約の入力検証
 * で落ちた結果が「取得に失敗した」として画面に出て、URL の打ち間違いと本当の障害が同じ
 * 見た目になる。
 *
 * @param termId 引く用語の id。読めなければ undefined
 * @param pjNumber 重ねる案件番号。渡さなければ master
 * @param side 案件の面。既定は変更後（案件を重ねた姿）
 * @param enabled 問い合わせるか。案件がこの面に用語を持たないと分かっているときは止める
 * @returns 取りうる値・状態遷移・単位・参照元つきの用語 1 件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語 1 件の詳細を、master または指定した案件の変更前・変更後の姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getTermDetail
 */
export function useTermDetail(
  termId: number | undefined,
  pjNumber?: number,
  side: PjSide = 'after',
  enabled = true,
): UseQueryResult<TermDetailOutput> {
  const locale = useSpecLocale();
  const sideKey = pjNumber === undefined ? null : side;
  return useQuery({
    queryKey: queryKeys.specGraph.getTermDetail(termId ?? 0, locale, pjNumber, sideKey),
    enabled: termId !== undefined && enabled,
    queryFn: () =>
      rpcClient.specGraph.getTermDetail({
        termId: termId ?? 0,
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side } }),
      }),
  });
}
